import { useState, useEffect } from "react";
import { apiFetch } from "../../../context/AuthContext";

// `localStorage.clientName` is written generically at login (the profile
// key-dump in useLoginForm.completeLoginFlow) but is ALSO re-written by the
// Admin Dashboard's own data hook — so an admin/manager who lands on that
// dashboard first always has it cached solid by the time they open Messages.
// A plain 'user' account goes straight to User_Account and never visits that
// dashboard, so if the login-time write hasn't landed yet (slow profile
// fetch, first-login timing, a cleared/partial cache) this hook used to bail
// out immediately and silently treat "not cached yet" as "feature disabled"
// — showing featureNotAvailable even though the superadmin had genuinely
// enabled it. Resolve clientName from the user's own profile as a fallback
// so this hook doesn't depend on some other screen having run first.
const resolveClientName = async (BASE_URL) => {
    try {
        const cached = localStorage.getItem("clientName");
        if (cached) return cached;
    } catch (error) {
        console.error("Error accessing localStorage for key: clientName", error);
    }

    try {
        const meRes = await apiFetch(`${BASE_URL}/users/me`, { method: "GET" });
        if (meRes.ok) {
            const meJson = await meRes.json();
            const resolved = meJson?.profile?.clientName || meJson?.clientName || "";
            if (resolved) {
                try {
                    localStorage.setItem("clientName", resolved);
                } catch (error) {
                    console.error("Error writing localStorage key: clientName", error);
                }
                return resolved;
            }
        }
    } catch (error) {
        console.error("Error resolving clientName from /users/me:", error);
    }

    return "";
};

const useModulePermissions = (BASE_URL) => {
    const [modulePermissions, setModulePermissions] = useState(null);
    const [loadingPermissions, setLoadingPermissions] = useState(true);

    useEffect(() => {
        let cancelled = false;

        const fetchModulePermissions = async () => {
            try {
                const clientName = await resolveClientName(BASE_URL);

                if (!clientName) {
                    if (!cancelled) setLoadingPermissions(false);
                    return;
                }

                const response = await apiFetch(
                    `${BASE_URL}/api/configurations/${encodeURIComponent(clientName)}`,
                    { method: 'GET' }
                );

                if (cancelled) return;

                if (response.ok) {
                    const data = await response.json();
                    setModulePermissions(data?.data);
                } else {
                    setModulePermissions({ privateMessaging: false });
                }
            } catch (error) {
                console.error("Error fetching module permissions:", error);
                if (!cancelled) setModulePermissions({ privateMessaging: false });
            } finally {
                if (!cancelled) setLoadingPermissions(false);
            }
        };

        fetchModulePermissions();
        return () => {
            cancelled = true;
        };
    }, [BASE_URL]);

    return { modulePermissions, loadingPermissions };
};

export default useModulePermissions;